import { Avatar, Box, Button, Typography } from '@mui/material'
import { isEmpty } from 'lodash'
import { useEffect, useState, type Dispatch, type SetStateAction } from 'react'

import { color } from 'config/color'
import { useMobileBreakpoints } from 'utils/hooks/useMobileBreakpoints'

import ElectricBoltIcon from '@mui/icons-material/ElectricBolt'
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment'
import WaterDropIcon from '@mui/icons-material/WaterDrop'
import { useAuth } from 'context'

const SERVICES = [
  {
    type: 'Electric Service',
    icon: ElectricBoltIcon,
    bgColor: color.electricity[50],
    iconColor: color.electricity[400],
    metric: 'kWh',
    value: 'electric'
  },
  {
    type: 'Water Service',
    icon: WaterDropIcon,
    bgColor: color.water[50],
    iconColor: color.water[400],
    metric: 'CF',
    value: 'water'
  },
  {
    type: 'Gas Service',
    icon: LocalFireDepartmentIcon,
    bgColor: color.gas[50],
    iconColor: color.gas[400],
    metric: 'CF',
    value: 'gas'
  }
]

interface IServiceButtonGroup {
  serviceType: string
  setServiceType: Dispatch<SetStateAction<string>>
}

export const ServiceButtonGroup = ({
  serviceType,
  setServiceType
}: IServiceButtonGroup): JSX.Element => {
  const { services } = useAuth()
  const isMobile = useMobileBreakpoints()
  const [availableServices, setAvailableServices] = useState<any>()

  useEffect(() => {
    setAvailableServices(
      SERVICES.filter(
        (servDetail) =>
          !isEmpty(
            services.find(
              (service) => service.toLowerCase() === servDetail.value
            )
          )
      )
    )
  }, [services])

  return (
    <Box
      display="flex"
      gap={2}
      flexDirection={isMobile ? 'column' : 'row'}
      mt={1.5}
      borderRadius={1}
    >
      {availableServices?.map((item: any, index: number) => (
        <Box
          component={Button}
          key={index}
          value={item.value}
          display="flex"
          alignItems="center"
          justifyContent="flex-start"
          sx={{
            '&:hover': {
              bgcolor:
                serviceType === item.value
                  ? color.background[900]
                  : color.white
            }
          }}
          bgcolor={
            serviceType === item.value ? color.background[900] : color.white
          }
          width={isMobile ? 'auto' : '100%'}
          gap={2}
          p={3}
          onClick={() => {
            setServiceType(item.value)
          }}
        >
          <Avatar
            sx={{
              bgcolor:
                serviceType === item.value
                  ? color.background[800]
                  : item.bgColor,
              width: isMobile ? 32 : 56,
              height: isMobile ? 32 : 56
            }}
          >
            {
              <item.icon
                sx={{
                  color:
                    serviceType === item.value ? color.white : item.iconColor,
                  width: isMobile ? 18 : 32,
                  height: isMobile ? 18 : 32
                }}
              />
            }
          </Avatar>
          <Typography
            fontSize={24}
            color={serviceType === item.value ? color.white : color.grey[700]}
            fontWeight={400}
            textTransform="none"
          >
            {item.type}
          </Typography>

          <Typography
            fontSize={18}
            color={color.grey[500]}
            fontWeight={400}
            textTransform="none"
          >
            ({item.metric})
          </Typography>
        </Box>
      ))}
    </Box>
  )
}
